import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import Tooltip from '../../components/helpers/DataToolTip';
import BaseButton from '../../components/helpers/Button';
import { useDirectorDashboardPusher, PROJECT_REPORT_UPDATED } from '../../hooks/useDirectorDashboardPusher';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { Button, ButtonsGroup } from '../../components/ButtonsGroup';
import { DateRangePicker } from '../../components/DateRangePicker';
import { ProjectsSidebar } from '../../components/DirectorDashboard/ProjectReport/ProjectsSidebar';
import { ProjectHeader } from '../../components/DirectorDashboard/ProjectReport/ProjectHeader';
import { SummaryTile } from '../../components/DirectorDashboard/ProjectReport/SummaryTile';
import { FirmRollupTable } from '../../components/DirectorDashboard/ProjectReport/FirmRollupTable';
import { DATE_ISO_FORMAT, MONTH_DAY_SHORT_FORMAT } from '../../utils/dateFormat';
import { loadProjects, loadProject, loadProjectStatistics, shareProjectLink } from '../../actions/directorDashboard';
import debounce from '../../utils/debounce';
import { detectBottom } from '../../utils/scrollbar';

import style from './style.module.css';

const SEARCH_DEBOUNCE_DELAY = 300;

dayjs.extend(weekOfYear);
dayjs.extend(quarterOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const getCurrentWeek = () => {
  const dateFrom = dayjs()
    .startOf('week')
    .add(1, 'day');
  const dateTo = dayjs()
    .endOf('week')
    .add(1, 'day');

  return { dateFrom, dateTo };
};

const PRESET_RANGES = {
  currentWeek: getCurrentWeek,
  lastWeek: () => ({
    dateFrom: dayjs()
      .startOf('week')
      .subtract(6, 'day'),
    dateTo: dayjs()
      .endOf('week')
      .subtract(6, 'day'),
  }),
  MTD: () => ({ dateFrom: dayjs().startOf('month'), dateTo: dayjs() }),
  lastMonth: () => ({
    dateFrom: dayjs()
      .subtract(1, 'month')
      .startOf('month'),
    dateTo: dayjs()
      .subtract(1, 'month')
      .endOf('month'),
  }),
  QTD: () => ({ dateFrom: dayjs().startOf('quarter'), dateTo: dayjs() }),
  lastQuarter: () => ({
    dateFrom: dayjs()
      .subtract(1, 'quarter')
      .startOf('quarter'),
    dateTo: dayjs()
      .subtract(1, 'quarter')
      .endOf('quarter'),
  }),
  YTD: () => ({ dateFrom: dayjs().startOf('year'), dateTo: dayjs() }),
  lastYear: () => ({
    dateFrom: dayjs()
      .subtract(1, 'year')
      .startOf('year'),
    dateTo: dayjs()
      .subtract(1, 'year')
      .endOf('year'),
  }),
};

const PRESET_LABELS = {
  currentWeek: ({ dateFrom, dateTo }) =>
    `Current Week (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  lastWeek: ({ dateFrom, dateTo }) =>
    `Last Week (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  MTD: ({ dateFrom, dateTo }) =>
    `Month to Date (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  lastMonth: ({ dateFrom, dateTo }) =>
    `Last Month (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  QTD: ({ dateFrom }) => `Quarter to Date (Q${dateFrom.quarter()})`,
  lastQuarter: ({ dateFrom }) => `Last Quarter (Q${dateFrom.quarter()})`,
  YTD: ({ dateFrom, dateTo }) =>
    `Year to Date (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  lastYear: ({ dateFrom }) => `Last Year (${dateFrom.format('YYYY')})`,
};

const findMatchingRangeKey = period => {
  if (!period.dateFrom || !period.dateTo) return null;

  const matchedKey = Object.keys(PRESET_RANGES).find(key => {
    const range = PRESET_RANGES[key]();

    return period.dateFrom.isSame(range.dateFrom, 'day') && period.dateTo.isSame(range.dateTo, 'day');
  });

  return matchedKey ?? null;
};

const ProjectReportContainer = ({
  projects,
  projectsLoading,
  projectsMeta,
  projectStatistics,
  projectStatisticsLoading,
  user,
  loadProjects,
  loadProject,
  loadProjectStatistics,
  shareProjectLink,
}) => {
  const { subscribe, unsubscribe } = useDirectorDashboardPusher(user);
  const [projectsQuery, setProjectsQuery] = useState({ page: 1, active: undefined, search: '' });
  const { page, active, search } = projectsQuery;
  const [firmRollupText, setFirmRollupText] = useState('');
  const [period, setPeriod] = useState(getCurrentWeek());
  const [id, setId] = useState(null);

  const activeRangeKey = useMemo(() => findMatchingRangeKey(period), [period]);

  const loadProjectStatisticsWithPeriod = useCallback(
    (projectId, { dateFrom, dateTo }, showLoading = true) => {
      loadProjectStatistics(
        {
          projectId,
          dateFrom: dateFrom.format(DATE_ISO_FORMAT),
          dateTo: dateTo.format(DATE_ISO_FORMAT),
        },
        showLoading,
      );
    },
    [loadProjectStatistics],
  );

  useEffect(() => {
    loadProjects({ page, active, search });
  }, [page, active, search]);

  const isPeriodComplete = Boolean(period.dateFrom && period.dateTo && id);

  useEffect(() => {
    if (isPeriodComplete) {
      loadProjectStatisticsWithPeriod(id, period);
    }
  }, [id, period, isPeriodComplete, loadProjectStatisticsWithPeriod]);

  useEffect(() => {
    subscribe(PROJECT_REPORT_UPDATED, ({ data }) => {
      if (data.project_id !== id) return;

      if (data.project_data_updated) {
        loadProject(id, false);
      }

      if (isPeriodComplete && data.project_stats_updated) {
        const eventDate = dayjs(data.date);

        if (eventDate.isSameOrAfter(period.dateFrom) && eventDate.isSameOrBefore(period.dateTo)) {
          loadProjectStatisticsWithPeriod(id, period, false);
        }
      }
    });

    return () => {
      unsubscribe(PROJECT_REPORT_UPDATED);
    };
  }, [id, period, isPeriodComplete, loadProject, loadProjectStatisticsWithPeriod]);

  useEffect(() => {
    const range = getCurrentWeek();

    setFirmRollupText(PRESET_LABELS.currentWeek(range));
    setPeriod(range);
  }, []);

  const handleGetStatistics = useCallback(value => {
    const getRange = PRESET_RANGES[value];

    if (!getRange) return;

    const range = getRange();

    setFirmRollupText(PRESET_LABELS[value](range));
    setPeriod(range);
  }, []);

  const handleDateRangeChange = useCallback(({ dateFrom, dateTo }) => {
    setPeriod({ dateFrom, dateTo });

    if (dateFrom && dateTo) {
      setFirmRollupText(
        `Custom Range (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
      );
    }
  }, []);

  const debouncedSetSearch = useMemo(
    () =>
      debounce(value => {
        setProjectsQuery(previousQuery => ({ ...previousQuery, page: 1, search: value }));
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const activeProject = useMemo(() => {
    if (!id || !projects) return null;

    return projects.find(project => project.get('id') === id);
  }, [id, projects]);

  const handleSearch = useCallback(
    value => {
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  const handleSearchClear = useCallback(() => {
    setProjectsQuery(previousQuery => ({ ...previousQuery, page: 1, search: '' }));
  }, []);

  const handleSelectProject = useCallback(projectId => {
    setId(projectId);
  }, []);

  const handleActivityChange = useCallback(value => {
    setProjectsQuery(previousQuery => ({ ...previousQuery, page: 1, active: value }));
  }, []);

  const hasMoreProjects = (projects?.size ?? 0) < (projectsMeta?.getIn(['pagination', 'total'], 0) ?? 0);

  const summaryTileDescription = useMemo(
    () => ({
      contactRate: `${projectStatistics.getIn(['atAGlance', 'contacts'], '—')} contacts / ${projectStatistics.getIn(
        ['atAGlance', 'approvedTargets'],
        '—',
      )} targets`,
      leadRate: `${projectStatistics.getIn(['atAGlance', 'leads'], '—')} leads / ${projectStatistics.getIn(
        ['atAGlance', 'approvedTargets'],
        '—',
      )} targets`,
      callsPerApproach: `${projectStatistics.getIn(['atAGlance', 'calls'], '—')} calls / ${projectStatistics.getIn(
        ['atAGlance', 'approaches'],
        '—',
      )} approaches`,
      approachesPerLead: `${projectStatistics.getIn(
        ['atAGlance', 'approaches'],
        '—',
      )} approaches / ${projectStatistics.getIn(['atAGlance', 'leads'], '—')} leads`,
      callsPerLead: `${projectStatistics.getIn(['atAGlance', 'calls'], '—')} calls / ${projectStatistics.getIn(
        ['atAGlance', 'leads'],
        '—',
      )} leads`,
      universeSize: 'Total approved targets in project',
    }),
    [projectStatistics],
  );

  const handleScroll = useCallback(
    event => {
      if (projectsLoading || !hasMoreProjects) return;
      if (!detectBottom(event.target)) return;

      setProjectsQuery(previousQuery => ({ ...previousQuery, page: previousQuery.page + 1 }));
    },
    [projectsLoading, hasMoreProjects],
  );

  const handleRefresh = useCallback(() => {
    if (!id) return;

    loadProject(id);
    loadProjectStatisticsWithPeriod(id, period);
  }, [id, period, loadProjectStatisticsWithPeriod]);

  const handleShareProjectLink = useCallback(
    data =>
      new Promise(resolve => {
        if (!isPeriodComplete) {
          resolve(null);

          return;
        }

        shareProjectLink(
          {
            ...data,
            projectId: id,
            dateFrom: period.dateFrom.format(DATE_ISO_FORMAT),
            dateTo: period.dateTo.format(DATE_ISO_FORMAT),
          },
          {
            afterSuccess: ({ response }) => {
              window.open(response.data.link, '_blank');
              resolve(response);
            },
            afterError: () => {
              resolve(null);
            },
          },
        );
      }),
    [id, period, isPeriodComplete, shareProjectLink],
  );

  return (
    <>
      <div className="row d-flex align-items-center pt10">
        <div className="col-md-4">
          <h3 className="text-primary-dark text-bold">Project Report</h3>
          <h6>Per-project KPI breakdowns. Pick a project on the left to drill into Analyst-level activity.</h6>
        </div>
        <div className="col-md-8 text-right">
          <div className={style.dateRangeToolbar}>
            <Tooltip content="Refresh">
              <BaseButton onClick={handleRefresh}>
                <i aria-hidden="true" className="fa fa-refresh mr0" />
              </BaseButton>
            </Tooltip>
            <DateRangePicker
              dateFrom={period.dateFrom}
              dateTo={period.dateTo}
              format="MMM D, YYYY"
              onChange={handleDateRangeChange}
            />
            <ButtonsGroup onChange={handleGetStatistics} value={activeRangeKey}>
              <Button label="Current Week" value="currentWeek" />
              <Button label="Last Week" value="lastWeek" />
              <Button label="MTD" value="MTD" />
              <Button label="Last Month" value="lastMonth" />
              <Button label="QTD" value="QTD" />
              <Button label="Last Quarter" value="lastQuarter" />
              <Button label="YTD" value="YTD" />
              <Button label="Last Year" value="lastYear" />
            </ButtonsGroup>
          </div>
        </div>
      </div>
      <div className="row mt5">
        <div className="col-sm-3 col-md-4 col-lg-4 col-xxl-3">
          <ProjectsSidebar
            activeProjectId={id}
            count={projectsMeta?.getIn(['pagination', 'total'], 0)}
            data={projects}
            isLoading={projectsLoading}
            onActivityChange={handleActivityChange}
            onScroll={handleScroll}
            onSearch={handleSearch}
            onSearchClear={handleSearchClear}
            onSelect={handleSelectProject}
          />
        </div>
        <div className="col-sm-9 col-md-8 col-lg-8 col-xxl-9">
          {id && (
            <div className={style.scrollableContent}>
              <ProjectHeader project={activeProject} />
              <div className="row mt20 mb20">
                <div className="col-md-12">
                  <div className={style.panelStatistic}>
                    <SummaryTile
                      description={summaryTileDescription.contactRate}
                      label="Contact rate"
                      value={`${projectStatistics.getIn(['atAGlance', 'contactRate'], 0)}%`}
                    />
                    <SummaryTile
                      description={summaryTileDescription.leadRate}
                      label="Lead Rate"
                      value={`${projectStatistics.getIn(['atAGlance', 'leadRate'], 0)}%`}
                    />
                    <SummaryTile
                      description={summaryTileDescription.callsPerApproach}
                      label="Calls per Approach"
                      value={`${projectStatistics.getIn(['atAGlance', 'callsPerApproach'], 0)}`}
                    />
                    <SummaryTile
                      description={summaryTileDescription.approachesPerLead}
                      label="Approaches per Lead"
                      value={`${projectStatistics.getIn(['atAGlance', 'approachesPerLead'], 0)}`}
                    />
                    <SummaryTile
                      description={summaryTileDescription.callsPerLead}
                      label="Calls per Lead"
                      value={`${projectStatistics.getIn(['atAGlance', 'callsPerLead'], 0)}`}
                    />
                    <SummaryTile
                      description={summaryTileDescription.universeSize}
                      label="Universe Size"
                      value={`${projectStatistics.getIn(['atAGlance', 'universeSize'], 0)}`}
                    />
                  </div>
                </div>
              </div>
              <div className="panel panel-default mb0">
                <div className="panel-heading d-flex align-items-center justify-content-space-between">
                  <h5 className={style.panelHeading}>
                    Firm Rollup — &nbsp;<span className="text-muted">{firmRollupText}</span>
                  </h5>
                </div>
                <FirmRollupTable
                  isLoading={projectStatisticsLoading}
                  onShareLink={handleShareProjectLink}
                  statistics={projectStatistics}
                  user={user}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

function mapStateToProps(state) {
  return {
    projects: state.directorDashboard.get('projects'),
    projectsLoading: state.directorDashboard.get('projectsLoading'),
    projectsMeta: state.directorDashboard.get('projectsMeta'),
    project: state.directorDashboard.get('project'),
    projectStatistics: state.directorDashboard.get('projectStatistics'),
    projectStatisticsLoading: state.directorDashboard.get('projectStatisticsLoading'),
    user: state.auth.get('user', null),
  };
}

export { ProjectReportContainer };
export default connect(
  mapStateToProps,
  {
    loadProjects,
    loadProject,
    loadProjectStatistics,
    shareProjectLink,
  },
  mergeProps,
  connectOptions,
)(memo(ProjectReportContainer));
